import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, Edit, FileText, Plus, Printer, RefreshCw, Search } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { AlertBanner, inputClass } from './accountingUi';
import { formatMoney, getFinancialYearId, printAccountingVoucher } from '../../utils/accountingHelpers';
import { getVoucherConfig } from '../../utils/accountingVouchers';
import DataColumnMenu from '../ui/DataColumnMenu';
import { columnHasActiveFilter, groupRows } from '../ui/columnMenuUtils';
import { useGroupCollapse, GroupByBanner, GroupHeaderLabel } from '../ui/groupCollapse';
import { PAGE_SIZE_OPTIONS, buildPageItems } from '../../utils/listPagination';
import { exportGroupedExcel } from '../../utils/excelExport';
import { buildListQueryFromColumnMenu } from '../../services/dataSource/listQuery';
import { fetchGridPage } from '../../services/dataSource/fetchGrid';

const VOUCHER_FIELD_MAP = {
  vouchernumber: 'VoucherNumber',
  date: 'Date',
  accountcode: 'Account.AccountCode',
  accountname: 'Account.Name',
  total: 'Total',
  remarks: 'Remarks',
};

const VOUCHER_COLUMNS = [
  { field: 'vouchernumber', title: 'Voucher No', widthClass: 'w-[12%]', hideable: true },
  { field: 'date', title: 'Date', widthClass: 'w-[10%]', hideable: true },
  { field: 'accountcode', title: 'Account Code', widthClass: 'w-[10%]', hideable: true },
  { field: 'accountname', title: 'Account Name', widthClass: 'w-[18%]', hideable: true },
  { field: 'total', title: 'Amount', widthClass: 'w-[10%]', hideable: true, align: 'right' },
  { field: 'remarks', title: 'Remarks', widthClass: 'w-[18%]', hideable: true },
  { field: 'actions', title: 'Options', widthClass: 'w-[8%]', hideable: true, sortable: false, filterable: false, groupable: false, align: 'center' },
];

const defaultVisibility = () =>
  VOUCHER_COLUMNS.reduce((acc, col) => {
    acc[col.field] = true;
    return acc;
  }, {});

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
};

const getCellValue = (row, field) => {
  switch (field) {
    case 'vouchernumber':
      return row.VoucherNumber || '-';
    case 'date':
      return formatDate(row.Date);
    case 'accountcode':
      return row.Account?.AccountCode || '-';
    case 'accountname':
      return row.Account?.Name || '-';
    case 'total':
      return formatMoney(row.Total);
    case 'remarks':
      return row.Remarks || '-';
    default:
      return row[field];
  }
};

const AccountingVoucherList = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const config = useMemo(() => getVoucherConfig(slug), [slug]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filtersByField, setFiltersByField] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(defaultVisibility);
  const [groupField, setGroupField] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const loadSeqRef = useRef(0);
  const pageSizeBeforeGroupRef = useRef(null);

  const fetchRows = useCallback(async ({
    page: pageArg,
    pageSize: pageSizeArg,
    keyword: keywordArg,
    sortField: sortFieldArg,
    sortDirection: sortDirectionArg,
    filters: filtersArg,
    groupField: groupFieldArg,
  } = {}) => {
    if (!config) return;
    const financialYearId = getFinancialYearId();
    if (!financialYearId) {
      setError('Financial year is not set. Log out and log in again, selecting the correct financial year.');
      setRows([]);
      setTotalCount(0);
      return;
    }

    const pageNum = pageArg ?? page;
    const size = pageSizeArg !== undefined ? pageSizeArg : pageSize;
    const keyword = keywordArg !== undefined ? keywordArg : activeKeyword;
    const sField = sortFieldArg !== undefined ? sortFieldArg : sortField;
    const sDir = sortDirectionArg !== undefined ? sortDirectionArg : sortDirection;
    const filters = filtersArg !== undefined ? filtersArg : filtersByField;
    const activeGroup = groupFieldArg !== undefined ? groupFieldArg : groupField;
    const isAll = size === 'all' || !!activeGroup;
    const take = isAll ? 10000 : Number(size);
    const skip = isAll ? 0 : (pageNum - 1) * Number(size);
    const seq = ++loadSeqRef.current;

    try {
      setLoading(true);
      setError('');
      const listQuery = buildListQueryFromColumnMenu({
        skip,
        take,
        sortField: sField,
        sortDirection: sDir,
        filtersByField: filters,
        fieldMap: VOUCHER_FIELD_MAP,
      });

      const { data, total } = await fetchGridPage(
        (body) =>
          MainService[config.apis.list]({
            ...body,
            FinancialYearId: financialYearId,
            Keyword: (keyword || '').trim() || null,
          }),
        listQuery
      );

      if (seq !== loadSeqRef.current) return;
      setRows(Array.isArray(data) ? data : []);
      setTotalCount(Number(total) || 0);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Load voucher list error:', err);
      setError(`Failed to load ${config.title.toLowerCase()} list`);
      setRows([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [config, page, pageSize, activeKeyword, sortField, sortDirection, filtersByField, groupField]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const onRefresh = (event) => {
      if (!event.detail?.slug || event.detail.slug === slug) {
        fetchRows();
      }
    };
    window.addEventListener('ainsoft:accounting-voucher-list-refresh', onRefresh);
    return () => window.removeEventListener('ainsoft:accounting-voucher-list-refresh', onRefresh);
  }, [slug, fetchRows]);

  const effectivePageSize = pageSize === 'all' ? Math.max(totalCount, 1) : Number(pageSize);
  const totalPages = pageSize === 'all' || groupField ? 1 : Math.max(1, Math.ceil(totalCount / effectivePageSize) || 1);
  const pageItems = useMemo(() => buildPageItems(page, totalPages), [page, totalPages]);
  const rangeFrom = totalCount === 0 ? 0 : (pageSize === 'all' || groupField ? 1 : (page - 1) * Number(pageSize) + 1);
  const rangeTo = totalCount === 0 ? 0 : (pageSize === 'all' || groupField ? totalCount : Math.min(page * Number(pageSize), totalCount));
  const colSpan = useMemo(
    () => VOUCHER_COLUMNS.filter((c) => columnVisibility[c.field] !== false).length + 1,
    [columnVisibility]
  );
  const activeFilterCount = useMemo(
    () => Object.keys(filtersByField).filter((f) => columnHasActiveFilter(filtersByField, f)).length,
    [filtersByField]
  );

  const displayGroups = useMemo(() => {
    if (!groupField) return [{ key: null, label: null, rows }];
    const mapped = rows.map((row) => ({ ...row, [groupField]: getCellValue(row, groupField) }));
    return groupRows(mapped, groupField);
  }, [rows, groupField]);

  const { isCollapsed, toggleGroup, collapseAll, expandAll } = useGroupCollapse(groupField);
  const groupKeys = useMemo(
    () => displayGroups.filter((g) => g.label != null).map((g) => g.key),
    [displayGroups]
  );

  const handleSearch = () => {
    const keyword = searchTerm.trim();
    setPage(1);
    setActiveKeyword(keyword);
    fetchRows({ page: 1, keyword });
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchTerm('');
    setActiveKeyword('');
    setSortField('');
    setSortDirection('asc');
    setFiltersByField({});
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    fetchRows({
      page: 1,
      keyword: '',
      sortField: '',
      sortDirection: 'asc',
      filters: {},
      groupField: null,
    });
  };

  const handlePageSizeChange = (value) => {
    const next = value === 'all' ? 'all' : Number(value);
    setPageSize(next);
    setPage(1);
    fetchRows({ page: 1, pageSize: next });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    fetchRows({ page: nextPage });
  };

  const handleColumnSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(1);
    fetchRows({ page: 1, sortField: field, sortDirection: direction });
  };

  const handleToggleColumn = (field) => {
    setColumnVisibility((prev) => {
      const nextVisible = { ...prev, [field]: prev[field] === false };
      const stillVisible = VOUCHER_COLUMNS.some((c) => nextVisible[c.field] !== false);
      if (!stillVisible) return prev;
      return nextVisible;
    });
  };

  const handleApplyColumnFilter = (field, filter) => {
    const nextFilters = { ...filtersByField, [field]: filter };
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const handleClearColumnFilter = (field) => {
    const nextFilters = { ...filtersByField };
    delete nextFilters[field];
    setFiltersByField(nextFilters);
    setPage(1);
    fetchRows({ page: 1, filters: nextFilters });
  };

  const handleGroupBy = (field) => {
    if (!groupField) {
      pageSizeBeforeGroupRef.current = { pageSize, page };
    }
    setGroupField(field);
    if (pageSize !== 'all') {
      setPage(1);
      setPageSize('all');
    }
    fetchRows({ page: 1, pageSize: 'all', groupField: field });
  };

  const handleUngroup = () => {
    const prev = pageSizeBeforeGroupRef.current;
    setGroupField(null);
    pageSizeBeforeGroupRef.current = null;
    if (prev) {
      setPageSize(prev.pageSize);
      setPage(prev.page);
      fetchRows({ page: prev.page, pageSize: prev.pageSize, groupField: null });
    } else {
      fetchRows({ groupField: null });
    }
  };

  const openVoucher = (row) => {
    if (row?.Id) navigate(`/home/accounting/vouchers/${slug}/${row.Id}`);
  };

  const handlePrint = async (row, event) => {
    event?.stopPropagation();
    if (!row?.Id || !config?.printType) return;
    try {
      setPrintingId(row.Id);
      setError('');
      const ok = await printAccountingVoucher(row.Id, config.printType, MainService, getApiBaseUrl);
      if (!ok) setError('Failed to print voucher');
    } catch (err) {
      console.error('Print voucher error:', err);
      setError('Failed to print voucher');
    } finally {
      setPrintingId(null);
    }
  };

  const exportToExcel = () => {
    try {
      const dataCols = VOUCHER_COLUMNS.filter((c) => c.field !== 'actions');
      exportGroupedExcel({
        sheetName: config?.shortTitle || 'Vouchers',
        fileName: `${slug}_vouchers_${new Date().toISOString().split('T')[0]}.xlsx`,
        headers: ['#', ...dataCols.map((c) => c.title)],
        groups: displayGroups,
        groupFieldTitle: VOUCHER_COLUMNS.find((c) => c.field === groupField)?.title || 'Group',
        mapRow: (row, serial) => [serial, ...dataCols.map((c) => getCellValue(row, c.field))],
      });
    } catch (err) {
      console.error('Export voucher list error:', err);
      setError(err?.message === 'No rows to export' ? 'No vouchers to export' : 'Failed to export Excel file');
    }
  };

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Unknown voucher type.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
                <p className="text-sm text-gray-500">Current financial year</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/home/accounting/vouchers/${slug}/new`)}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              New voucher
            </button>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative min-w-[240px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                className={`${inputClass} pl-10`}
                placeholder="Search by voucher number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSearch}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                <Search className="h-4 w-4" />
                Search
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={exportToExcel}
                disabled={rows.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        <AlertBanner type="error" message={error} />

        <div className="relative overflow-auto" style={{ height: 520 }}>
          {loading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/75 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-gray-600">Loading vouchers...</span>
              </div>
            </div>
          )}

          {(activeFilterCount > 0 || sortField) && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
              <span>
                {sortField
                  ? `Sorted by ${VOUCHER_COLUMNS.find((c) => c.field === sortField)?.title || sortField} (${sortDirection})`
                  : 'Filters active'}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} column filter(s)` : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  const prev = pageSizeBeforeGroupRef.current;
                  setFiltersByField({});
                  setSortField('');
                  setSortDirection('asc');
                  setGroupField(null);
                  pageSizeBeforeGroupRef.current = null;
                  setPage(1);
                  if (prev && prev.pageSize !== pageSize) {
                    setPageSize(prev.pageSize);
                  }
                  fetchRows({
                    page: 1,
                    pageSize: prev?.pageSize ?? pageSize,
                    sortField: '',
                    sortDirection: 'asc',
                    filters: {},
                    groupField: null,
                  });
                }}
                className="text-primary hover:underline"
              >
                Clear sort & filters
              </button>
            </div>
          )}

          {groupField && (
            <GroupByBanner
              groupTitle={VOUCHER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}
              groupKeys={groupKeys}
              onUngroup={handleUngroup}
              onCollapseAll={collapseAll}
              onExpandAll={expandAll}
            />
          )}

          <table className="data-grid-table w-full min-w-[1100px]">
            <thead className="data-grid-thead sticky top-0 z-10">
              <tr>
                <th className="data-grid-th data-grid-th-serial w-12 min-w-[3rem] px-3 py-2.5 text-left">#</th>
                {VOUCHER_COLUMNS.map((column) =>
                  columnVisibility[column.field] === false ? null : (
                    <DataColumnMenu
                      key={column.field}
                      column={column}
                      columns={VOUCHER_COLUMNS}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                      columnVisibility={columnVisibility}
                      onToggleColumn={handleToggleColumn}
                      filtersByField={filtersByField}
                      onApplyFilter={handleApplyColumnFilter}
                      onClearFilter={handleClearColumnFilter}
                      groupField={groupField}
                      onGroupBy={handleGroupBy}
                      onUngroup={handleUngroup}
                      align={column.align}
                    />
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {totalCount === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="px-6 py-12 text-center text-gray-500">
                    No vouchers found.
                  </td>
                </tr>
              ) : (
                (() => {
                  let serial = 0;
                  return displayGroups.flatMap((group) => {
                    const block = [];
                    if (group.label != null) {
                      block.push(
                        <tr key={`group-${group.key}`} className="bg-gray-100">
                          <td colSpan={colSpan} className="px-4 py-2">
                            <GroupHeaderLabel
                              title={VOUCHER_COLUMNS.find((c) => c.field === groupField)?.title}
                              label={group.label}
                              count={group.rows.length}
                              collapsed={isCollapsed(group.key)}
                              onToggle={() => toggleGroup(group.key)}
                            />
                          </td>
                        </tr>
                      );
                    }
                    if (group.label != null && isCollapsed(group.key)) {
                      return block;
                    }
                    group.rows.forEach((row, index) => {
                      serial += 1;
                      const serialNo = groupField || pageSize === 'all' ? serial : rangeFrom + serial - 1;
                      block.push(
                        <tr
                          key={row.Id || `${group.key}-${index}`}
                          className="cursor-pointer transition-colors hover:bg-gray-50"
                          onClick={() => openVoucher(row)}
                        >
                          <td className="table-cell-serial px-4 py-2 text-sm text-gray-600">{serialNo}</td>
                          {VOUCHER_COLUMNS.map((column) =>
                            columnVisibility[column.field] === false ? null : (
                              <td
                                key={column.field}
                                className={`px-4 py-2 text-sm text-gray-700 ${
                                  column.align === 'right' ? 'text-right' : ''
                                } ${column.align === 'center' ? 'text-center' : ''}`}
                                onClick={column.field === 'actions' ? (e) => e.stopPropagation() : undefined}
                              >
                                {column.field === 'vouchernumber' ? (
                                  <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold tracking-wide text-slate-800">
                                    {row.VoucherNumber || '-'}
                                  </span>
                                ) : column.field === 'total' ? (
                                  <span className="font-semibold text-gray-900">{formatMoney(row.Total)}</span>
                                ) : column.field === 'actions' ? (
                                  <div className="flex justify-center gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => handlePrint(row, e)}
                                      disabled={printingId === row.Id}
                                      className="rounded-lg bg-slate-50 p-2 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                      title="Print"
                                    >
                                      <Printer className={`h-4 w-4 ${printingId === row.Id ? 'animate-pulse' : ''}`} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openVoucher(row);
                                      }}
                                      className="rounded-lg bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  getCellValue(row, column.field)
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      );
                    });
                    return block;
                  });
                })()
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>
                {totalCount === 0 ? '0 vouchers' : `${rangeFrom}–${rangeTo} of ${totalCount}`}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter(s)` : ''}
                {groupField ? ` · grouped by ${VOUCHER_COLUMNS.find((c) => c.field === groupField)?.title || groupField}` : ''}
              </span>
              <label className="flex items-center gap-2">
                <span className="text-gray-500">Rows</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  disabled={loading || !!groupField}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={String(opt)} value={opt}>
                      {opt === 'all' ? 'All' : opt}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!groupField && pageSize !== 'all' && (
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                {pageItems.map((item, idx) =>
                  item === '…' ? (
                    <span key={`ellipsis-${idx}`} className="px-1.5 text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      disabled={loading}
                      onClick={() => goToPage(item)}
                      className={`min-w-[2rem] rounded-lg px-2 py-1.5 text-sm transition-colors ${
                        item === page
                          ? 'bg-[#46955A] font-medium text-white'
                          : 'border border-gray-200 bg-white text-gray-700 hover:bg-[#f0f9f4]'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={page >= totalPages || loading}
                  onClick={() => goToPage(page + 1)}
                  className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingVoucherList;
